<template>
  <div class="route-to-admin">
    <!-- Selected Email and Assignment Section -->
     <SelectedEmailAndAssignment 
      :emailData="currentEmail"
      :assignment="selectedAssignment"
      :initiallyCollapsed="false"
      @assignmentChanged="onAssignmentChanged"
    />

    <!-- Requested by section -->
    <div class="requested-by">
      <span class="requested-by-text">Requested by {{ userDisplayName }} on {{ formatDate(new Date()) }}</span>
    </div>

    <!-- Admin Request Field -->
    <div class="form-field">
      <label class="field-label">Admin Request*</label>
        <v-select v-model="selectedAdminRequest" :items="changeRequestCategories" item-title="name" item-value="id"
          variant="outlined" density="compact" hide-details return-object
        :menu-props="{ closeOnContentClick: true, maxWidth: '100%'}" placeholder="Admin Request"
        :error="hasError('selectedAdminRequest')" 
        :class="{ 'field-error': hasError('selectedAdminRequest') }"
        @blur="touch('selectedAdminRequest')">
          <template v-slot:append-inner>
          <IconProvider name="chevron-down" size="16" color="#666" />
          </template>

          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props" :title="item.raw.name" :value="item.raw" class="select-item"
              :class="{ 'placeholder-item': item.raw.id === -1 }" @click="selectAdminRequest(item.raw)">
              <template v-slot:title>
                <span class="item-text" style="font-size: 13px !important;">{{ item.raw.name }}</span>
              </template>
            </v-list-item>
          </template>

          <template v-slot:selection="{ item }">
            <span class="selection-text" :class="{ 'placeholder-text': item.raw.id === -1 }">
              {{ item.raw.name }}
            </span>
          </template>
        </v-select>
        <div v-if="hasError('selectedAdminRequest')" class="error-message">
          {{ getError('selectedAdminRequest') }}
        </div>
    </div>

    <!-- Dynamic Instruction Labels -->
   
    
    <!-- Request Type -->

    <div class="form-field" v-if="selectedAdminRequest && selectedAdminRequest?.id === 206">
      <label class="field-label">Request Type*</label>
        <v-select v-model="selectedRequestType" :items="requestTypes" item-title="name" item-value="id"
          variant="outlined" density="compact" hide-details return-object
        :menu-props="{ closeOnContentClick: true, maxWidth: '100%' }" placeholder="Request Type"
        :error="hasError('selectedRequestType')" 
        :class="{ 'field-error': hasError('selectedRequestType') }"
        @blur="touch('selectedRequestType')">
          <template v-slot:append-inner>
          <IconProvider name="chevron-down" size="16" color="#666" />
          </template>

          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props" :title="item.raw.name" :value="item.raw" class="select-item"
              :class="{ 'placeholder-item': item.raw.id === -1 }" @click="selectRequestType(item.raw)">
              <template v-slot:title>
                <span class="item-text" style="font-size: 13px !important;">{{ item.raw.name }}</span>
              </template>
            </v-list-item>
          </template>

          <template v-slot:selection="{ item }">
            <span class="selection-text" :class="{ 'placeholder-text': item.raw.id === -1 }">
              {{ item.raw.name }}
            </span>
          </template>
        </v-select>
        <div v-if="hasError('selectedRequestType')" class="error-message">
          {{ getError('selectedRequestType') }}
        </div>
    </div>

    <!-- Modification Type -->

    <div class="form-field" v-if="selectedAdminRequest && selectedAdminRequest?.id === 174">
      <label class="field-label">Modification Type*</label>
        <v-select v-model="selectedModificationType" :items="modificationTypes" item-title="name" item-value="id"
          variant="outlined" density="compact" hide-details return-object
          multiple chips closable-chips
        :menu-props="{ closeOnContentClick: false, maxWidth: '100%' }" placeholder="Modification Type"
        :error="hasError('selectedModificationType')" 
        :class="{ 'field-error': hasError('selectedModificationType') }"
        @blur="touch('selectedModificationType')">
          <template v-slot:append-inner>
          <IconProvider name="chevron-down" size="16" color="#666" />
          </template>

          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props" :title="item.raw.name" :value="item.raw" class="select-item"
              :class="{ 'placeholder-item': item.raw.id === -1 }">
              <template v-slot:title>
                <span class="item-text" style="font-size: 13px !important;">{{ item.raw.name }}</span>
              </template>
            </v-list-item>
          </template>

          <template v-slot:selection="{ item }">
            <span class="selection-text" :class="{ 'placeholder-text': item.raw.id === -1 }">
              {{ item.raw.name }}
            </span>
          </template>
        </v-select>
        <div v-if="hasError('selectedModificationType')" class="error-message">
          {{ getError('selectedModificationType') }}
        </div>
    </div>

    <template v-for="(label, index) in visibleInstructionLabels" :key="`instruction-${index}`">
      <div class="instruction-label instructions-class" v-html="label.label"></div>
    </template>
    <!-- Comments Field -->
    <div class="form-field">
      <label class="field-label">Comments</label>
      <textarea v-model="note" class="comments-textarea" placeholder="Additional Comments" rows="5"
        :class="{ 'field-error': hasError('comments') }"
        @blur="touch('comments')"></textarea>
      <div v-if="hasError('comments')" class="error-message">
        {{ getError('comments') }}
      </div>
    </div>

    <!-- Attach Files -->
    <div class="form-field">
      <AttachFileDetails :config="attachFileConfig" :assignmentData="selectedAssignment"
        @collections="onFilesAttached" />
    </div>


    <div class="cc-title">Add Additional Recipients</div>

    <div class="cc-list">
      <div class="cc-row-flex" v-for="option in ccOptions" :key="option.label">
        <v-checkbox
          v-model="checkedCC[option.label]"
          :id="option.label"
          hide-details
          dense
          color="#003f2d"
          class="cc-checkbox-flex"
        />
        <span class="cc-label-flex">{{ option.label }}</span>
        <span class="cc-names-flex">{{ option.names.join(', ') }}</span>
      </div>
    </div>

   <IsLoadingComponent v-if="showLoading" />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { mapGetters, mapActions } from 'vuex';
import SelectedAssignment from '@/components/actions/assignment/SelectedAssignment.vue';
import SelectedEmailAndAssignment from '@/components/commons/SelectedEmailAndAssignment.vue';
import IconProvider from '@/components/commons/IconProvider.vue';
import AttachFileDetails from '@/components/AttachFileDetails.vue';
import IsLoadingComponent from '@/components/IsLoadingComponent.vue';
import { handleRecentActions ,postToCommonOL, fetchUserDetails, fetchAllocations } from '@/service/engagement.js';
import { markEmailAsCompleteNew } from '@/service/Actions.js';
import { ACTION_TYPES } from '@/constants';
import _ from 'lodash';
import instructionLabelsConfig from './sendAdminRequestInstructions.json';
import { useValidation } from '@/utils/useValidation.js';

export default {
  name: 'SendAdminRequest',
  components: {
    SelectedAssignment,
    SelectedEmailAndAssignment,
    IconProvider,
    AttachFileDetails,
    IsLoadingComponent
  },
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    // Initialize validation composable
    const validation = useValidation();
    const { rules, getError, hasError, touch, validateField, validateAll, clearField } = validation;

    // Form field refs
    const selectedAdminRequest = ref(null);
    const selectedRequestType = ref(null);
    const selectedModificationType = ref([]);
    const note = ref('');

    // Initialize with default values
    const selectTeamOption = { id: -1, name: 'Select a team...' };
    

    // Validation rules for each field
    const validationRules = computed(() => ({
      selectedAdminRequest: [rules.required],
      selectedRequestType: [
        rules.requiredIf(() => 
          selectedAdminRequest.value && selectedAdminRequest.value.id === 206
        )
      ],
      selectedModificationType: [
        rules.requiredIf(() => 
          selectedAdminRequest.value && selectedAdminRequest.value.id === 174
        ),
        (value) => {
        if (selectedAdminRequest.value && selectedAdminRequest.value.id === 174) {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return 'This Field is required';
          }
        }
        return null;
    }
      ],
      comments: [
        (value) => {
          if (!value || value.length <= 250) {
            return null; // Valid
          }
          return 'Max character count of 250 reached'; // Invalid
        }
      ]
    }));

    // Field values for validation
    const fieldValues = computed(() => ({
      selectedAdminRequest: selectedAdminRequest.value,
      selectedRequestType: selectedRequestType.value,
      selectedModificationType: selectedModificationType.value,
      comments: note.value
    }));

    // Watch for changes and validate
    watch(selectedAdminRequest, (newValue, oldValue) => {
      if (oldValue && newValue && oldValue.id !== newValue.id) {
        // Reset dependent fields when admin request changes
        selectedRequestType.value = null;
        selectedModificationType.value = [];
        clearField('selectedRequestType');
        clearField('selectedModificationType');
      }
      validateField('selectedAdminRequest', newValue, validationRules.value.selectedAdminRequest);
       if (newValue?.id === 174) {
        setTimeout(() => {
          touch('selectedModificationType');
          validateField('selectedModificationType', selectedModificationType.value, validationRules.value.selectedModificationType);
        }, 0);
      }
  
      // Immediately validate request type if it becomes visible
      if (newValue?.id === 206) {
        setTimeout(() => {
          touch('selectedRequestType');
          validateField('selectedRequestType', selectedRequestType.value, validationRules.value.selectedRequestType);
        }, 0);
      }   
    });

    watch(selectedRequestType, (newValue) => {
      validateField('selectedRequestType', newValue, validationRules.value.selectedRequestType);
    });

    watch(selectedModificationType, (newValue) => {
      validateField('selectedModificationType', newValue, validationRules.value.selectedModificationType);
    });

    watch(note, (newValue) => {
      if (validation.touched.comments) {
        validateField('comments', newValue, validationRules.value.comments);
      }
    });

    // Validation methods
    const validateForm = () => {
      touch('selectedAdminRequest');
      
      if (selectedAdminRequest.value && selectedAdminRequest.value.id === 206) {
        touch('selectedRequestType');
      }
      
      if (selectedAdminRequest.value && selectedAdminRequest.value.id === 174) {
        touch('selectedModificationType');
      }

      return validateAll(validationRules.value, fieldValues.value);
    };

    // Check if form can be submitted
    const canSubmitRequest = computed(() => {
      if (!selectedAdminRequest.value || selectedAdminRequest.value.id === -1) {
        return false;
      }

      if (selectedAdminRequest.value.id === 206) {
        if (!selectedRequestType.value || selectedRequestType.value.id === -1) {
          return false;
        }
      }

      if (selectedAdminRequest.value.id === 174) {
        if (!selectedModificationType.value || !Array.isArray(selectedModificationType.value) || selectedModificationType.value.length === 0) {
          return false;
        }
      }

      return true;
    });

    return {
      // Form fields
      selectedAdminRequest,
      selectedRequestType,
      selectedModificationType,
      note,
      selectTeamOption,
      // Validation
      getError,
      hasError,
      touch,
      validateForm,
      canSubmitRequest,

      // Validation state
      validationErrors: validation.errors,
      validationTouched: validation.touched
    };
  },
  data() {  
    return {
      selectedTeam: null,
      requestType: null, 
      modificationType:null,
      applyToAssignmentList: [],
      attachedFiles: [],
      notifyTeam: false,
      showLoading: false,
      markAsProcessed: true,
      checkedCC: {},
      allocationsData: { names: [], emails: [] }, // Store fetched allocations
      isLoaded: false,
      hasAccess: false,
      INVOICE_CREATION_REQUEST: 206,
      INVOICE_REPRINT_RESEND: 189,
      INVOICE_EDIT_REQUEST: 205,
      APPRAISER_ALLOCATIONS_EDIT_CONFIRMED_ALLOCATIONS: 190,
      BILLING_TEAM: 188,
      INVOICING_TEAM: 195,
      ROW_OPERATIONS_TEAM: 196,
      APPRAISER_ALLOCATIONS_MODIFY_BOOKED_ALLOCATIONS: 210,
    }
  },
  created() {
    this.selectedTeam = this.selectTeamOption;

    if (this.config && this.config.processingOptions && this.config.processingOptions.markAsProcessed) {
      this.markAsProcessed = this.config.processingOptions.markAsProcessed.default;
    }

    // Fetch all lookups when component is created
    this.$store.dispatch('lookup/fetchAllLookups');
    
    // Optionally fetch engagement data if assignment is already selected
    if (this.selectedAssignment?.viewEventId) {
      this.fetchEngagementDataSilently();
      // Fetch allocations for Valuation Team
      this.fetchAndStoreAllocations();
    }
  },
  computed: {
    ...mapGetters({
      selectedAssignment: "assignments/selectedAssignment",
      getLookupByType: "lookup/getLookupByType",
      getAllLookups: "lookup/getAllLookups",
      currentEmail: "email/currentEmail",
      getUserDetails: "email/getUserDetails",
      engagementId: "assignments/engagementId",
      assignmentOverviewData: "assignments/assignmentOverviewData",
    }),
    userDisplayName() {
      try {
        // Try to get user info from store if available
        const userDetails = this.getUserDetails;
        if (userDetails && typeof userDetails === 'object') {
          const displayName = userDetails.fullName || userDetails.name || userDetails.displayName || userDetails.email;
          return displayName || 'User';
        }
        return 'User';
      } catch (error) {
        console.error('Error getting current user:', error);
        return 'User';
      }
    },
    selectItems() {
      if (this.getLookupByType('tops2Teams')) {
        return [
          // this.selectTeamOption,
          ...this.getLookupByType('tops2Teams')
        ];
      }
      return [this.selectTeamOption]
    },
    attachFileConfig() {
      return {
        label: 'Attach Additional File(s)',
        btnClass: 'attach-files-btn',
        attachFileLabel: 'Attach Additional File(s)',
        disableAutoCloseDialog: false,
        allowedExtensions: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.jpg', '.jpeg', '.png'],
      }
    },
    modificationTypes() {
      return this.getLookupItems('modificationType', true);
    },
    requestTypes() {
      return this.getLookupItems('adminReqSubType', true);
    },
    changeRequestCategories() {
      return this.getLookupItems('changeRequestCategory', true);
    },
    ccOptions() {
      const a = this.selectedAssignment;
      if (!a) return [];
      
      return [
        {
          label: "Originator",
          checked: false,
          names: a.originator ? [a.originator] : [],
          email: [a.originatorEmail]
        },
        {
          label: "Valuer(s)",
          checked: false,
          names: a.valuers ? a.valuers.map(v => v.fullName) : [],
          email: a.valuers ? a.valuers.map(v => v.emailAddress) : []
        },
        {
          label: "Valuation Team",
          checked: false,
          names: this.allocationsData.names,
          email: this.allocationsData.emails
        }
      ];
    },
    // Dynamic instruction labels based on JSON config
    instructionLabelsConfig() {
      return instructionLabelsConfig.filter(item => 
        item.compType === 'LabelComponent' && 
        item.config.class === 'instructions-class'
      );
    },
    visibleInstructionLabels() {
      return this.instructionLabelsConfig.filter(labelConfig => {
        // Only show labels that have requestCategoryId conditions but NOT requestTypeId conditions
        const hasRequestCategory = labelConfig.config.showIf.some(condition => 
          condition.fieldId === 'changeRequest.requestCategoryId'
        );
        const hasRequestType = labelConfig.config.showIf.some(condition => 
          condition.fieldId === 'changeRequest.requestTypeId'
        );
        
        return hasRequestCategory && !hasRequestType && this.checkShowIfConditions(labelConfig.config.showIf);
      }).map(labelConfig => ({
        label: labelConfig.config.label,
        class: labelConfig.config.class
      }));
    },
  },
  watch: {
    canSubmitRequest: {
      handler(newValue) {
        this.$emit('isValidForSubmit', newValue);
      },
      immediate: true,
    },
    ccOptions: {
      handler(newOptions) {
        // Initialize checkedCC based on ccOptions
        const newCheckedCC = {};
        newOptions.forEach(option => {
          if (option.label) {
            newCheckedCC[option.label] = option.checked;
          }
        });
        this.checkedCC = newCheckedCC;
      },
      immediate: true
    },
    selectedAssignment: {
      async handler(newAssignment, oldAssignment) {
        // Fetch engagement data when assignment changes
        if (newAssignment && newAssignment.viewEventId && 
            (!oldAssignment || newAssignment.viewEventId !== oldAssignment.viewEventId)) {
          this.fetchEngagementDataSilently();
          // Fetch allocations for Valuation Team
          await this.fetchAndStoreAllocations();
        }
      },
      immediate: false
    },
    // Watch for Valuation Team checkbox changes to auto-select others
    'checkedCC.Valuation Team': {
      handler(newValue, oldValue) {
        if (newValue && !oldValue) {
          // User checked Valuation Team - auto-select Originator and Valuer(s)
          this.$nextTick(() => {
            this.checkedCC = {
              ...this.checkedCC,
              'Originator': true,
              'Valuer(s)': true
            };
          });
        }
        // Note: We don't auto-unselect when Valuation Team is unchecked
        // as per requirements
      }
    },
    // Watch checkedCC for other logic
    checkedCC: {
      handler(newCheckedCC, oldCheckedCC) {
        // Handle the case where user manually unchecks Originator or Valuer(s)
        // while Valuation Team is still checked
        if (newCheckedCC && oldCheckedCC) {
          const valuationTeamChecked = newCheckedCC['Valuation Team'];
          
          // If Valuation Team is checked but user manually unchecked Originator,
          // we allow it (as per requirements)
          if (valuationTeamChecked) {
            // Logic is handled automatically by individual checkbox states
            // No additional action needed here
          }
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions({
      uploadFiles: "assignments/uploadFiles",
      addMetricsData: "assignments/addMetricsData",
      convertToEmlFormData: "email/convertToEmlFormData",
      add: "notification/add",
      fetchAssignmentOverviewData: "assignments/fetchAssignmentOverviewData",
      setEngagementId: "assignments/setEngagementId",
    }),
    async getAllocations() {
      // Get all allocations from the new API endpoint, excluding primary valuer
      const allocations = { names: [], emails: [] };
      
      if (!this.selectedAssignment?.viewEventId) {
        console.log('⚠️ No viewEventId available for fetching allocations');
        return allocations;
      }
      
      try {
        // Fetch allocations from the new API endpoint
        const allocationData = await fetchAllocations(this.selectedAssignment.viewEventId);
        
        if (!allocationData || !Array.isArray(allocationData)) {
          console.log('⚠️ No allocation data received from API');
          return allocations;
        }
        
        console.log('🔍 Processing allocations:', {
          totalAllocations: allocationData.length,
          allocations: allocationData
        });
        
        // Get primary valuer info to exclude from allocations
        const primaryValuerId = this.assignmentOverviewData?.valuationAssignmentInformation?.primaryValuer?.userId;
        
        // Define valid role IDs and their names
        const validRoleIds = [1, 2, 3, 4, 5]; // Primary Valuer, Valuer, Reviewer, Coordinator, Participant
        const getRoleName = (roleId) => {
          switch (roleId) {
            case 1: return "Primary Valuer";
            case 2: return "Valuer"; 
            case 3: return "Reviewer";
            case 4: return "Coordinator";
            case 5: return "Participant";
            default: return "Unknown Role";
          }
        };
        
        // Process each allocation from appraiserAllocationBillingModel
        allocationData.forEach(allocation => {
          const roleId = allocation.jobAppraiserAllocationRoleId;
          
          // Include only users with valid role IDs (1-5)
          if (validRoleIds.includes(roleId) && 
              allocation.emailAddress && 
              allocation.fullName) {
            
            const roleName = getRoleName(roleId);
            
            // For Primary Valuer (roleId = 1), they typically go to "To" field, not CC
            // But we'll include them in the Valuation Team list and let the backend decide
            // Skip primary valuer only if they're already designated as the primary valuer in assignment data
            if (roleId === 1 && primaryValuerId && allocation.userId === primaryValuerId) {
              console.log('⏭️ Skipped Primary Valuer (already in To field):', {
                name: allocation.fullName,
                role: roleName,
                userId: allocation.userId
              });
              return; // Skip this iteration
            }
            
            // Add unique names and emails for all other valid roles
            if (!allocations.names.includes(allocation.fullName)) {
              allocations.names.push(allocation.fullName);
            }
            if (!allocations.emails.includes(allocation.emailAddress)) {
              allocations.emails.push(allocation.emailAddress);
            }
            
            console.log('✅ Added allocation:', {
              name: allocation.fullName,
              email: allocation.emailAddress,
              roleId: roleId,
              roleName: roleName,
              userId: allocation.userId
            });
          } else if (!validRoleIds.includes(roleId)) {
            console.log('⏭️ Skipped invalid role:', {
              name: allocation.fullName,
              roleId: roleId,
              roleName: getRoleName(roleId)
            });
          }
        });
        
        console.log('✅ Final allocations for Valuation Team:', allocations);
        return allocations;
        
      } catch (error) {
        console.error('❌ Error fetching allocations:', error);
        // Fallback to empty allocations if API fails
        return allocations;
      }
    },
    getLookupItems(lookupType, shouldSort = false, fallbackItem = null) {
      const allLookups = this.getAllLookups;
      if (allLookups && Array.isArray(allLookups)) {
        const lookup = allLookups.find(lookup => lookup.lookupType === lookupType);
        if (lookup && lookup.lookups) {
          const transformedItems = lookup.lookups.map(item => ({
            id: item.keyId,
            name: item.key,
            sortOrder: item.sortOrder
          }));
          
          if (shouldSort) {
            const sortedItems = _.sortBy(transformedItems, 'sortOrder');
            return fallbackItem ? [fallbackItem, ...sortedItems] : [...sortedItems];
          }
          
          return [...transformedItems];
        }
      }
      return fallbackItem ? [fallbackItem] : [];
    },
    selectTeam(team) {
      this.selectedTeam = team;
    },
    selectAdminRequest(request) {
      this.selectedAdminRequest = request;
    },
    selectRequestType(request) {
      this.selectedRequestType = request
    },
    checkShowIfConditions(showIfConditions) {
      if (!showIfConditions || !Array.isArray(showIfConditions)) {
        return false;
      }

      // All conditions must be met (AND logic)
      return showIfConditions.every(condition => {
        const { fieldId, op, value } = condition;
        
        // Get the actual value based on fieldId
        let actualValue = null;
        
        switch (fieldId) {
          case 'changeRequest.requestCategoryId':
            actualValue = this.selectedAdminRequest?.id;
            break;
          case 'changeRequest.requestTypeId':
            actualValue = this.selectedRequestType?.id;
            break;
          case 'changeRequest.modificationTypeId':
            actualValue = this.selectedModificationType?.id;
            break;
          default:
            return false;
        }

        // Skip if no value selected
        if (actualValue === null || actualValue === undefined || actualValue === -1) {
          return false;
        }

        // Apply the operation
        switch (op) {
          case 'in':
            return Array.isArray(value) && value.includes(actualValue);
          case 'eq':
          case '==':
          case '===':
            return actualValue === value;
          case 'neq':
          case '!=':
          case '!==':
            return actualValue !== value;
          default:
            return false;
        }
      });
    },
    onFilesAttached(files) {
      this.attachedFiles = files;
      console.log('Files attached:', files);
    },

    formatDate(date) {
      try {
        const options = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        };
        return date.toLocaleDateString('en-US', options);
      } catch (error) {
        console.error('Date formatting error:', error);
        return new Date().toLocaleDateString('en-US');
      }
    },

    getWorkFileId(value) {
      if (!value) return null
      let arr
      if (value.includes('?')) {
        let boxArr = value.split('?')
        arr = boxArr[0].split('/')
      } else {
        arr = value.split('/')
      }
      return arr && arr.length > 0 ? arr[arr.length - 1] : null
    },

    closeDialog() {
      this.$emit('close');
    },

    getBoxFileDetails(assignmentId) {
      let documentDetails = []
      if (this.attachedFiles && Array.isArray(this.attachedFiles)) {
        this.attachedFiles.forEach(file => {
          if (file) {
            documentDetails.push({
              originalFileName: file.name,
              boxFileId: file.id,
              documentSize: file.size,
              assignmentId: assignmentId
            });
          }
        });
      }
      return documentDetails;
    },

    async saveAdminRequestActions() {
      const payload = {
        action: ACTION_TYPES.ROUTE_TO_ADMIN,
        fileId: this.selectedAssignment?.code,
        entityIds: [this.selectedAssignment?.viewEventId]
      };
      await handleRecentActions(payload, ACTION_TYPES.ROUTE_TO_ADMIN);
    },
    async saveRecentActions() {
      const payload = {
        action: ACTION_TYPES.ROUTE_TO_ADMIN,
        entityIds: [this.selectedAssignment?.viewEventId],
        fileId: this.selectedAssignment?.code
      };
      await handleRecentActions(payload, ACTION_TYPES.ROUTE_TO_ADMIN);
    },

    async fetchEngagementDataSilently() {
      // Assignment overview data is now preloaded when assignment is selected
      // Check if we already have the data in the store
      if (this.selectedAssignment?.viewEventId && !this.engagementId && !this.assignmentOverviewData) {
        try {
          await this.fetchAssignmentOverviewData(this.selectedAssignment.viewEventId);
        } catch (error) {
          // Silently fail - we have fallback logic in sendAdminRequest
          console.warn('Could not pre-fetch engagement data:', error);
        }
      }
    },
    getForwardingTeam(overviewData) {
      let forwardingTeamId = overviewData?.serviceProvidedData?.serviceLine?.keyId === 50706 ? 196: 194;
      if (
        this.selectedAdminRequest?.id ===  this.APPRAISER_ALLOCATIONS_EDIT_CONFIRMED_ALLOCATIONS ||
        this.selectedAdminRequest?.id ===  this.APPRAISER_ALLOCATIONS_MODIFY_BOOKED_ALLOCATIONS
      ) {
        forwardingTeamId = this.BILLING_TEAM
      } else if (
        this.selectedAdminRequest?.id === this.INVOICE_CREATION_REQUEST ||
        this.selectedAdminRequest?.id === this.INVOICE_REPRINT_RESEND ||
        this.selectedAdminRequest?.id === this.INVOICE_EDIT_REQUEST
      ) {
        if ( overviewData?.serviceProvidedData?.serviceLine?.keyId === 50706 ) {
          forwardingTeamId = this.ROW_OPERATIONS_TEAM
        } else {
          forwardingTeamId = this.INVOICING_TEAM
        }
      }
      return forwardingTeamId;
    },
    async fetchAndStoreAllocations() {
      // Fetch allocations and store in data property
      try {
        this.allocationsData = await this.getAllocations();
      } catch (error) {
        console.error('Error fetching allocations for Valuation Team:', error);
        this.allocationsData = { names: [], emails: [] };
      }
    },
    async sendAdminRequest() {
      // Validate form before submission
      if (!this.validateForm()) {
        return;
      }

      const startTime = performance.now();
      let finalStatus = 'Completed';
      let errorMessage = null;

      const metricsPayload = {
        entityId: this.selectedAssignment?.viewEventId,
        fileId: this.selectedAssignment?.code,
        actionTaken: ACTION_TYPES.ROUTE_TO_ADMIN,
      };

      try {
        this.showLoading = true;
        const assignmentId = this.selectedAssignment?.viewEventId;
        const overviewData = await this.fetchAssignmentOverviewData(assignmentId);
        const userDetails = await fetchUserDetails(Office.context.mailbox.userProfile.emailAddress);

        const payload = {
          changeRequest: {
            changeRequestTypeId: 179,
            requestCategoryId: this.selectedAdminRequest?.id,
            modificationTypeIds: Array.isArray(this.selectedModificationType) 
          ? this.selectedModificationType.map(item => item.id)
          : [],
            requestTypeId: this.selectedRequestType?.id,
            createdByEmail: Office.context.mailbox.userProfile.emailAddress
          },
          assignmentIds: [ this.selectedAssignment?.viewEventId],
          engagementId: overviewData?.sourceSystemId,
          changeRequestActions: [
            {
              note: this.note,
              changeRequestItemStatusId: 183,
              changeRequestItemStatus: "Requested",
              auditUser: {
                emailAddress: userDetails.emailAddress,
                fullName: userDetails.fullName
              },
              assignedTo: {
                emailAddress: "",
                fullName: ""
              },
              changeRequestForwardId: this.getForwardingTeam(overviewData),
              documents: this.getBoxFileDetails(assignmentId)
            }
          ],
            ccValuersForEmail: this.checkedCC['Valuer(s)'] || false, 
            ccOriginatorsForEmail: this.checkedCC['Originator'] || false,
            ccValuationTeamForEmail: this.checkedCC['Valuation Team'] || false,
            isRequestFromPlugin: true,
          viewEventId: this.selectedAssignment?.viewEventId
        };
        await postToCommonOL('/change-request/create', payload);
        await this.saveRecentActions();
        if(this.markAsProcessed) {
          markEmailAsCompleteNew(
            'completeNotification',
            'Admin Request Created successfully',
            undefined,
            true
          );
        }
        this.add({
          type: 'success',
          message: 'Admin Request Created successfully',
          timeout: 5
        });
        this.closeDialog();
      } catch (error) {
        console.error('Error sending admin request:', error);
        finalStatus = 'Error';
        errorMessage = error.message;

        // Show error notification
        this.add({
          type: 'error',
          message: `Failed to submit admin request: ${error.message}`,
          timeout: 5
        });
      } finally {
        this.showLoading = false;

        const endTime = performance.now();
        const manualProcessingTime = Math.round(endTime - startTime);

        // Create explicit payload with processingTime
        const explicitPayload = {
          entityId: metricsPayload.entityId,
          fileId: metricsPayload.fileId,
          actionTaken: metricsPayload.actionTaken,
          status: finalStatus,
          processingTime: Math.max(0, manualProcessingTime),
          timestamp: new Date().toISOString()
        };

        if (errorMessage) {
          explicitPayload.errorMessage = errorMessage;
        }

        // Defensive: Ensure processingTime is always a valid number (never null)
        if (explicitPayload.processingTime == null || isNaN(explicitPayload.processingTime)) {
          explicitPayload.processingTime = 0;
        }

        await this.addMetricsData(explicitPayload);
      }
    },
  }
}
</script>

<style scoped>
/* Main Container - match application standard */
.route-to-admin {
  font-family: Segoe UI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica Neue, sans-serif;
}

/* Selected Section */
.selected-section {
  margin-bottom: 10px;
}

/* Requested by section */
.requested-by {
  margin-bottom: 10px;
}
.requested-by-text {
  font-size: 13px;
  color: #1A1A1AA6;
  font-weight: 500;
}

/* Form Fields */
.form-field {
  margin-bottom: 10px;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 400;
  font-size: 16px;
  color: #000;
  font-family: Calibre-R;
}

/* Field Error Styles */
.field-error :deep(.v-field) {
  border-color: #f44336 !important;
}

.field-error :deep(.v-field--focused) {
  border-color: #f44336 !important;
}

.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 400;
}

/* Override Vuetify select spacing */
.route-to-admin :deep(.v-input) {
  margin-bottom: 0 !important;
}

.route-to-admin :deep(.v-input__details) {
  margin-top: 0 !important;
  padding-top: 0 !important;
  min-height: auto !important;
}

.select-item:nth-child(odd) {
  background-color: #ffffff !important;
}

.select-item:nth-child(even) {
  background-color: #f5f5f5 !important;
}

.selection-text {
  font-size: 16px;
  color: #000;
  font-weight: 400;
}

.placeholder-text {
  color: #1A1A1AA6;
  font-size: 16px;
  font-weight: 400;
}

/* Comments Textarea - match ForwardToAppraiser styling */
.comments-textarea {
  width: 100%;
  height: 120px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 12px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  max-height: 200px;
}

.comments-textarea:focus {
  outline: none;
  border-color: #003F2D;
  box-shadow: none;
}
.comments-textarea.field-error {
  border-color: #f44336 !important;
}

.comments-textarea::placeholder {
  color: #6a6a6a;
  font-style: normal;
}
/* CC Options Section */
.cc-title {
  font-weight: 400;
  font-size: 16px;
  color: #000;
  font-family: Calibre-R;
  font-style: Regular;
}
.cc-desc {
  font-size: 16px;
  color: #6a6a6a;
  font-weight: 400;
}
.cc-list {
  margin-top: 5px;
  margin-left: 5px;
}
.cc-row-flex {
  display: flex;
  align-items: center; /* Center all items vertically */
  min-height: 28px;
  line-height: 1;
}
.cc-checkbox-flex {
  margin-right: 8px;
  min-width: 32px;
  margin-top: 0;
  padding: 0 !important;
  height: auto; /* Remove fixed height for better alignment */
  display: flex;
  align-items: center;
}
::v-deep .cc-checkbox-flex .v-input--selection-controls__input {
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;
  display: flex;
  align-items: center;
}
::v-deep .cc-checkbox-flex .v-icon, /* Vuetify 2 */
::v-deep .cc-checkbox-flex .v-icon__svg, /* Vuetify 3 */
::v-deep .cc-checkbox-flex svg {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  vertical-align: middle !important;
  display: inline-flex !important;
}
.cc-label-flex {
  font-size: 14px;
  margin-right: 24px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  color: #1A1A1A;
}
.cc-names-flex {
  width: 100%;
  text-align: right;
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1;
  word-break: break-word;
  white-space: normal;
  display: block;
}

/* Instruction Labels */
.instruction-label {
  margin-bottom: 10px;
  padding: 0;
  background-color: transparent;
  border: none;
  font-size: 16px;
  color: #000;
  font-weight: 600;
}

.instruction-label.instructions-class {
  background-color: transparent;
  border: none;
  font-weight: 500;
}

.instruction-label.comment-class {
  background-color: transparent;
  border: none;
  margin-top: 8px;
  font-style: italic;
}

.instruction-label :deep(ul) {
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
}

.instruction-label :deep(li) {
  margin-bottom: 4px;
  line-height: 1.4;
}

.route-to-admin :deep(.v-select.v-input--error .v-field) {
  border-color: #f44336 !important;
}

.route-to-admin :deep(.v-select.v-input--error .v-field__outline) {
  color: #f44336 !important;
}

.route-to-admin :deep(.v-select.v-input--error .v-field__outline__start),
.route-to-admin :deep(.v-select.v-input--error .v-field__outline__notch),
.route-to-admin :deep(.v-select.v-input--error .v-field__outline__end) {
  border-color: #f44336 !important;
}

</style>